import React, { useEffect, useState } from "react";
import _get from "lodash.get";
import { FormikErrors, FormikValues, useFormikContext } from "formik";
import { SectorsMulti } from "./SectorsMulti";
import { Select } from "../Select";
import { useSectors } from "newui/store/common-types";
import { LabelValue } from "newui/types/LabelValue.type";
import { SectorOption } from "newui/types/SectorOption.type";

export interface SectorsComponentProps {
	name: string;
	className?: string;
	clearable?: boolean;
	companyId?: number;
	defaultValue?: string | string[];
	includeAllOption?: boolean;
	isSearchBox?: boolean;
	label?: string;
	noLabel?: boolean;
	placeholder?: string;
	required?: boolean;
	showProjectsCount?: boolean; // Note: keeping original typo for consistency
	singleValue?: boolean;
	styles?: any;
	testId?: string;
	value?: LabelValue<number> | LabelValue<number>[];
	values: Record<string, any>;
	onChange?: (option: LabelValue<number> | LabelValue<number>[] | null) => void;
	setFieldValue?: (
		field: string,
		value: any,
		shouldValidate?: boolean
	) => Promise<void | FormikErrors<FormikValues>>;
}

export const Sectors: React.FC<SectorsComponentProps> = ({
	name,
	singleValue = false,
	values,
	value,
	includeAllOption,
	onChange,
	showProjectsCount,
	testId,
	label,
	required,
	noLabel,
	className,
	clearable,
	placeholder,
	companyId,
	...props
}) => {
	const { setFieldValue } = useFormikContext();
	const [options, setOptions] = useState([]);
	const { isLoading, values: allSectorData } = useSectors(companyId);

	useEffect(() => {
		const getLabel = (opt: SectorOption): string => {
			return showProjectsCount &&
				opt.projects_count !== null &&
				opt.projects_count !== undefined
				? `${opt.name} (${opt.projects_count})`
				: opt.name;
		};

		if (isLoading) {
			return;
		}

		const allOptions = showProjectsCount ? allSectorData.map((option: SectorOption) => ({
			value: option.id,
			label: getLabel(option),
		})) : [...allSectorData];

		if (includeAllOption) {
			if (!allOptions.find((opt) => opt.value === 999)) {
				allOptions.unshift({ label: "Show All", value: 999 });
			}
		}

		setOptions(allOptions);
	}, [allSectorData, includeAllOption, showProjectsCount, isLoading]);

	const SelectComponent = singleValue ? Select : SectorsMulti;

	// Create proper label/value obj based on form values
	let valueObj: LabelValue<number> | LabelValue<number>[] | undefined;

	if (!singleValue) {
		// Multi-select case
		if (values && Array.isArray(values[name])) {
			valueObj = values[name]
				.map((id: number) => options.find((option) => option.value === id))
				.filter(Boolean) as LabelValue<number>[];
		} else {
			valueObj = [];
		}
	} else {
		// Single select case
		const fieldValue = _get(values, name);
		valueObj = options.find((option) => option.value === fieldValue);
	}

	const handleOnChange = (option): void => {
		// only if singleValue - multi select handles this itself
		if (singleValue) {
			setFieldValue(name, option ? option.value : "");
		}

		if (onChange) {
			onChange(option);
		}
	};

	return (
		// @ts-ignore
		<SelectComponent
			options={options}
			name={name}
			setFieldValue={setFieldValue}
			label={label || "Sectors"}
			required={required}
			value={valueObj}
			noLabel={noLabel}
			className={className}
			isClearable={clearable ?? true}
			customOnChange={handleOnChange}
			testId={testId}
			placeholder={placeholder}
			isLoading={isLoading}
			{...props}
		/>
	);
};
